import React, { useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

const StaticGoogleMap = ({
  data,
  centerData,
  preview,
  region = "IN",
  createdWidget,
  refWidth,
  PdfDownloadStatus,
  noteExist,
  insightsExist,
  widgetDiscription,
}) => {
  const authParams = useSelector((state) => state?.Authentication?.authParams);
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const apiKey = "AIzaSyDGqipm4Lsoq2WkGQDoH1_Iu6eXPjiuE5A";

  // Generate static map URL with optimizations
  const staticMapUrl = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return null;
    }
    // Filter valid coordinates
    const validData = data.filter(
      (item) =>
        item &&
        typeof item.lat === "number" &&
        typeof item.lng === "number" &&
        !isNaN(item.lat) &&
        !isNaN(item.lng) &&
        item.lat >= -90 &&
        item.lat <= 90 &&
        item.lng >= -180 &&
        item.lng <= 180
    );

    if (validData.length === 0) {
      return null;
    }

    // Use first valid point as center or provided centerData
    const centerLat = centerData?.lat || validData[0].lat;
    const centerLng = centerData?.lng || validData[0].lng;
    const center = `${centerLat},${centerLng}`;

    // Reasonable map size (consider container constraints)
    const mapWidth = window?.innerWidth || Math.min(800, 640); // Google limit is 640px for free tier
    const mapHeight = window?.innerHeight || Math.min(600, 640);
    const size = `${mapWidth}x${mapHeight}`;

    // Limit markers to prevent URL length issues (Google has 8192 char limit)
    const maxMarkers = 50; // Conservative limit
    const limitedData = validData.slice(0, maxMarkers);

    // Build markers string more efficiently
    const markersArray = limitedData.map((item, index) => {
      const label = String(item?.name || "")?.charAt(0) || "L";
      return `markers=color:red|label:${label}|${item.lat},${item.lng}`;
    });

    const markersString = markersArray.join("&");
    const regionParam = region ? `&region=${region}` : "";

    const url = `https://maps.googleapis.com/maps/api/staticmap?center=${center}&zoom=5&size=${size}&maptype=roadmap&${markersString}${regionParam}&key=${apiKey}`;

    // Check URL length (Google limit is ~8192 characters)
    if (url.length > 8000) {
      console.warn("StaticGoogleMap: URL too long, reducing markers");
      // Fallback with fewer markers
      const reducedMarkers = limitedData
        .slice(0, 20)
        .map(
          (item, index) =>
            `markers=color:red|label:${
              String(item?.name || "")?.charAt(0) || "L"
            }|${item.lat},${item.lng}`
        )
        .join("&");

      return `https://maps.googleapis.com/maps/api/staticmap?center=${center}&zoom=5&size=${size}&maptype=roadmap&${reducedMarkers}${regionParam}&key=${apiKey}`;
    }

    return url;
  }, [data, centerData, region, apiKey, PdfDownloadStatus]);

  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
    setImageError(false);
  }, []);

  const handleImageError = useCallback((e) => {
    console.error("StaticGoogleMap: Failed to load image", e);
    setIsLoading(false);
    setImageError(true);
  }, []);

  // Early return for invalid data
  if (!staticMapUrl) {
    return (
      <div
        style={{
          padding: "20px",
          textAlign: "center",
          color: "#666",
          border: "1px dashed #ccc",
          borderRadius: "4px",
        }}
      >
        <p>No valid location data available for map display</p>
      </div>
    );
  }
  return (
    <div style={{ position: "relative" }}>
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1,
            background: "rgba(255, 255, 255, 0.8)",
            padding: "10px",
            borderRadius: "4px",
          }}
        >
          Loading map...
        </div>
      )}

      {imageError ? (
        <div
          style={{
            padding: "20px",
            textAlign: "center",
            color: "#d32f2f",
            border: "1px solid #f44336",
            borderRadius: "4px",
            backgroundColor: "#ffebee",
          }}
        >
          <p>
            Failed to load map. Please check your internet connection and try
            again.
          </p>
          <button
            onClick={() => {
              setImageError(false);
              setIsLoading(true);
            }}
            style={{
              padding: "8px 16px",
              backgroundColor: "#1976d2",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      ) : (
        <img
          style={{
            marginLeft: authParams?.ppt ? "20%" : "0rem",
            width: authParams?.ppt ? "60%" : "100%",
            height: authParams?.ppt
              ? "100%"
              : PdfDownloadStatus
              ? "68.5rem"
              : "100%",
            display: isLoading ? "none" : "block",
          }}
          src={staticMapUrl}
          alt="Static Google Map showing location markers"
          onLoad={handleImageLoad}
          onError={handleImageError}
          crossOrigin="anonymous"
        />
      )}
    </div>
  );
};

export default StaticGoogleMap;
